import { z } from 'zod/v4';

import { QUANTITY_UNITS } from '../backendTypes';

export enum IssueNameEnum {
    NO_DESIGNATOR_AND_QUANTITY = 'NO_DESIGNATOR_AND_QUANTITY',
    NO_DESIGNATOR_COLUMN = 'NO_DESIGNATOR_COLUMN',
    NO_QUANTITY_COLUMN = 'NO_QUANTITY_COLUMN',
    NO_MPN_COLUMN = 'NO_MPN_COLUMN',
    NO_HEADER_ROW = 'NO_HEADER_ROW',
    NO_DNP_COLUMN = 'NO_DNP_COLUMN',
    NO_GENERIC_PART_SUGGESTION_BECAUSE_INDUCTOR_DESIGNATOR = 'NO_GENERIC_PART_SUGGESTION_BECAUSE_INDUCTOR_DESIGNATOR',
    DESIGNATOR_AND_QUANTITY_DO_NOT_MATCH_TEMPLATE = 'DESIGNATOR_AND_QUANTITY_DO_NOT_MATCH_TEMPLATE',

    AMBIGUOUS_QUANTITIES_TEMPLATE = 'AMBIGUOUS_QUANTITIES_TEMPLATE',
    MANY_QUANTITIES_NO_DESIGNATOR = 'MANY_QUANTITIES_NO_DESIGNATOR',
    NO_QUANTITY_MANY_DESIGNATORS = 'NO_QUANTITY_MANY_DESIGNATORS',
    MULTIPLE_QUANTITY_DESIGNATOR_MATCHES = 'MULTIPLE_QUANTITY_DESIGNATOR_MATCHES',
    COULD_NOT_RESOLVE_DESIGNATORS_AND_QUANTITY = 'COULD_NOT_RESOLVE_DESIGNATORS_AND_QUANTITY',
    UNKNOWN_QUANTITY_UNIT = 'UNKNOWN_QUANTITY_UNIT',

    CONTRADICTING_DNP_INFORMATION_TEMPLATE = 'CONTRADICTING_DNP_INFORMATION_TEMPLATE',
    CONTRADICTING_DNP_AND_QUANTITY = 'CONTRADICTING_DNP_AND_QUANTITY',
    MANUFACTURER_DOES_NOT_MATCH_PART_OPTIONS_ALTERNATIVE_NAME = 'MANUFACTURER_DOES_NOT_MATCH_PART_OPTIONS_ALTERNATIVE_NAME',
    MANUFACTURER_DOES_NOT_MATCH_PART_OPTIONS = 'MANUFACTURER_DOES_NOT_MATCH_PART_OPTIONS',
    MISSING_REQUIRED_TECHNICAL_PARAMETER = 'MISSING_REQUIRED_TECHNICAL_PARAMETER',
    UNRECOGNIZED_TOLERANCE_VALUE = 'UNRECOGNIZED_TOLERANCE_VALUE',

    ASSUME_TOTAL_QUANTITY_PROVIDED_TEMPLATE = 'ASSUME_TOTAL_QUANTITY_PROVIDED_TEMPLATE',

    IGNORED_MANUFACTURER = 'IGNORED_MANUFACTURER',
    INHERITED_MANUFACTURER = 'INHERITED_MANUFACTURER',
    ASSUME_SMT_IMPERIAL_SYSTEM = 'ASSUME_SMT_IMPERIAL_SYSTEM',
    PARTIAL_MPN_MATCH = 'PARTIAL_MPN_MATCH',
    AGGREGATED_LINES_SAME_DESIGNATORS = 'AGGREGATED_LINES_SAME_DESIGNATORS',
    AGGREGATED_LINES_SAME_CONTENT = 'AGGREGATED_LINES_SAME_CONTENT',
    AGGREGATED_LINES_QUANTITY_CONTINUATION = 'AGGREGATED_LINES_QUANTITY_CONTINUATION',
    GENERIC_PART_SUGGESTION_BECAUSE_MANUFACTURER_GIVEN = 'GENERIC_PART_SUGGESTION_BECAUSE_MANUFACTURER_GIVEN',
    MULTI_LEVEL_BOM_STRUCTURE_DETECTED = 'MULTI_LEVEL_BOM_STRUCTURE_DETECTED',
    EMPTY_EXCEL_LINES = 'EMPTY_EXCEL_LINES',

    // Frontend issues
    NUMBER_OF_LINES = 'NUMBER_OF_LINES',
    NO_BOM_ITEM_FOUND = 'NO_BOM_ITEM_FOUND',
    DUPLICATE_DESIGNATORS_FOUND = 'DUPLICATE_DESIGNATORS_FOUND',
    NO_PART_OPTIONS = 'NO_PART_OPTIONS',
}

export enum Level {
    Error = 'Error',
    Warning = 'Warning',
    Notification = 'Notification',
}
const LevelRuntype = z.enum(Level);

export enum MissingRequirementsEnum {
    resistance = 'resistance',
    capacitance = 'capacitance',
    package = 'package',
    dielectric = 'dielectric',
}

export type BomImporterIssue = z.infer<typeof BomImporterIssueRuntype>;
export const BomImporterIssueRuntype = z
    .object({
        level: LevelRuntype,
    })
    .and(
        z.union([
            z.object({
                name: z.literal(IssueNameEnum.MULTIPLE_QUANTITY_DESIGNATOR_MATCHES),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_QUANTITY_COLUMN),
            }),
            z.object({
                name: z.literal(IssueNameEnum.MULTI_LEVEL_BOM_STRUCTURE_DETECTED),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_DESIGNATOR_AND_QUANTITY),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_DNP_COLUMN),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_GENERIC_PART_SUGGESTION_BECAUSE_INDUCTOR_DESIGNATOR),
            }),
            z.object({
                name: z.literal(IssueNameEnum.MANY_QUANTITIES_NO_DESIGNATOR),
            }),
            z.object({
                name: z.literal(IssueNameEnum.MANUFACTURER_DOES_NOT_MATCH_PART_OPTIONS),
                content: z.object({
                    manufacturer_primary_name: z.string(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.MANUFACTURER_DOES_NOT_MATCH_PART_OPTIONS_ALTERNATIVE_NAME),
                content: z.object({
                    manufacturer_matched_alternative_name: z.string(),
                    manufacturer_primary_name: z.string(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.MISSING_REQUIRED_TECHNICAL_PARAMETER),
                content: z.object({
                    generic_part_type: z.string(),
                    missing_requirement: z.array(z.enum(MissingRequirementsEnum)),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.UNRECOGNIZED_TOLERANCE_VALUE),
                content: z.object({
                    raw_value: z.string(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.CONTRADICTING_DNP_INFORMATION_TEMPLATE),
                content: z.object({
                    extracted_dnps: z.array(z.boolean()),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.CONTRADICTING_DNP_AND_QUANTITY),
            }),
            z.object({
                name: z.literal(IssueNameEnum.AMBIGUOUS_QUANTITIES_TEMPLATE),
                content: z.object({
                    quantities: z.array(z.number()),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.DESIGNATOR_AND_QUANTITY_DO_NOT_MATCH_TEMPLATE),
                content: z.object({
                    num_designators: z.number(),
                    quantity: z.number(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.IGNORED_MANUFACTURER),
                content: z.object({
                    provided_manufacturer: z.string(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.INHERITED_MANUFACTURER),
                content: z.object({
                    inherited_manufacturer: z.string(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.ASSUME_SMT_IMPERIAL_SYSTEM),
                content: z.object({
                    package_str: z.string(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.ASSUME_TOTAL_QUANTITY_PROVIDED_TEMPLATE),
                content: z.object({
                    quantity: z.number(),
                    num_designators: z.number(),
                    unit: z.enum(QUANTITY_UNITS),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.DUPLICATE_DESIGNATORS_FOUND),
                content: z.object({
                    duplicatedDesignators: z.array(z.string()),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.PARTIAL_MPN_MATCH),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NUMBER_OF_LINES),
                content: z.object({
                    numberOfLines: z.number(),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.AGGREGATED_LINES_SAME_DESIGNATORS),
            }),
            z.object({
                name: z.literal(IssueNameEnum.AGGREGATED_LINES_SAME_CONTENT),
            }),
            z.object({
                name: z.literal(IssueNameEnum.AGGREGATED_LINES_QUANTITY_CONTINUATION),
            }),
            z.object({
                name: z.literal(IssueNameEnum.UNKNOWN_QUANTITY_UNIT),
                // Content is nullish so payloads from older backends (no unit string) still parse.
                content: z.object({ unit: z.string().nullish() }).nullish(),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_PART_OPTIONS),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_HEADER_ROW),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_MPN_COLUMN),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_DESIGNATOR_COLUMN),
            }),
            z.object({
                name: z.literal(IssueNameEnum.COULD_NOT_RESOLVE_DESIGNATORS_AND_QUANTITY),
                content: z.object({
                    num_designators: z.number(),
                    quantities: z.array(z.number()),
                    designators: z.array(z.string()),
                }),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_BOM_ITEM_FOUND),
            }),
            z.object({
                name: z.literal(IssueNameEnum.NO_QUANTITY_MANY_DESIGNATORS),
            }),
            z.object({
                name: z.literal(IssueNameEnum.GENERIC_PART_SUGGESTION_BECAUSE_MANUFACTURER_GIVEN),
            }),
            z.object({
                name: z.literal(IssueNameEnum.EMPTY_EXCEL_LINES),
            }),
        ]),
    );
